/**
 * BOGO voucher (type "4") item-picker helpers.
 *
 * When a BOGO voucher is applied but the cart cannot satisfy the deal
 * (empty cart / no matching items), the website opens a Buy/Get picker —
 * a separate flow modeled on the type-3 voucher picker, but driven by
 * `buyConfig` / `getConfig` instead of `selectedCategories`.
 *
 * This module converts each config into a "section" whose lists follow the
 * exact conventions the existing (type-3 generic) voucher filter plumbing
 * expects (`voucherFilterView` redux payload + `engineShowSpecialItem`):
 * arrays of `{ _id, name, isActive }` entries where only `isActive: true`
 * entries count.
 */

const getActive = (list) =>
  (list || []).filter((entry) => entry && entry.isActive === true);

// All selectable "sizes" of a menu item: plain sizes plus the submodifiers
// of complex (itemType 1) items, tagged with their parent modifier id.
const getItemSizes = (menuItem) => {
  const plainSizes = menuItem?.selectedSizes || [];
  const modifierSizes = (menuItem?.selectedModifiers || []).flatMap(
    (modifier) =>
      (modifier?.subModifiers || []).map((subModifier) => ({
        ...subModifier,
        modifierId: modifier._id,
      }))
  );
  return [...plainSizes, ...modifierSizes];
};

const buildSection = (config, isGetConfig, allMenuItems, label, key) => {
  const activeCategories = getActive(config?.categories);
  const activeItems = getActive(config?.menuItems);
  const activeSizes = getActive(config?.menuSizes);
  const activeSubModifiers = getActive(config?.subModifiers);
  const anyItem = isGetConfig && config?.anyItem === true;
  const capRaw = config?.maxItemPrice;
  const maxItemPrice =
    isGetConfig &&
    capRaw !== null &&
    capRaw !== undefined &&
    capRaw !== '' &&
    Number(capRaw) > 0
      ? Number(capRaw)
      : null;

  // Resolve the full menu items this section allows (same precedence as the
  // BOGO discount engine: anyItem > explicit menuItems > categories > all).
  let allowedItems;
  if (anyItem) {
    allowedItems = [...(allMenuItems || [])];
  } else if (activeItems.length > 0) {
    const allowedIds = new Set(activeItems.map((item) => item._id));
    allowedItems = (allMenuItems || []).filter((item) =>
      allowedIds.has(item._id)
    );
  } else if (activeCategories.length > 0) {
    const allowedCategoryIds = new Set(
      activeCategories.map((category) => category._id)
    );
    allowedItems = (allMenuItems || []).filter((item) =>
      allowedCategoryIds.has(item.categoryId)
    );
  } else {
    allowedItems = [...(allMenuItems || [])];
  }

  const hasSizeFilter = activeSizes.length > 0 || activeSubModifiers.length > 0;
  const allowedSizeIds = hasSizeFilter
    ? new Set(
        [...activeSizes, ...activeSubModifiers].map((size) => size._id)
      )
    : null;

  // maxItemPrice is a qualification cap (same rule as the discount engine):
  // a size above the cap simply does not qualify.
  const sizeQualifies = (size) =>
    (!allowedSizeIds || allowedSizeIds.has(size?._id)) &&
    (maxItemPrice === null || Number(size?.price) <= maxItemPrice);

  // Keep only items with at least one qualifying size.
  allowedItems = allowedItems.filter((menuItem) =>
    getItemSizes(menuItem).some(sizeQualifies)
  );

  // Size filter payload. When the config carries an explicit size filter we
  // pass it through unchanged. Otherwise ("any size") we synthesize the
  // lists from the qualifying sizes, because the downstream filter/popup
  // plumbing needs explicit size entries to work with.
  let menuSizes = activeSizes;
  let subModifiers = activeSubModifiers;
  if (!hasSizeFilter) {
    const sizesById = {};
    const subModifiersById = {};
    allowedItems.forEach((menuItem) => {
      (menuItem?.selectedSizes || []).forEach((size) => {
        if (sizeQualifies(size)) {
          sizesById[size._id] = {
            _id: size._id,
            name: size.name,
            isActive: true,
          };
        }
      });
      (menuItem?.selectedModifiers || []).forEach((modifier) => {
        (modifier?.subModifiers || []).forEach((subModifier) => {
          if (sizeQualifies(subModifier)) {
            subModifiersById[subModifier._id] = {
              _id: subModifier._id,
              name: subModifier.name,
              isActive: true,
              modifierId: modifier._id,
            };
          }
        });
      });
    });
    menuSizes = Object.values(sizesById);
    subModifiers = Object.values(subModifiersById);
  }

  // Categories payload: explicit config categories when set, otherwise the
  // categories the allowed items belong to (needed because the menu filter
  // only renders categories present in this list).
  const categories =
    activeCategories.length > 0 && !anyItem
      ? activeCategories.map((category) => ({
          _id: category._id,
          name: category.name,
          isActive: true,
        }))
      : Object.values(
          allowedItems.reduce((acc, item) => {
            if (item.categoryId && !acc[item.categoryId]) {
              acc[item.categoryId] = {
                _id: item.categoryId,
                name: item.categoryName || '',
                isActive: true,
              };
            }
            return acc;
          }, {})
        );

  // Short human description for an empty slot box.
  let description = '';
  if (anyItem) {
    description = 'Any item';
  } else {
    const sizeNames = [...activeSizes, ...activeSubModifiers]
      .map((size) => size.name)
      .filter(Boolean);
    const categoryNames = activeCategories
      .map((category) => category.name)
      .filter(Boolean);
    description = [sizeNames.join('/'), categoryNames.join(' or ')]
      .filter(Boolean)
      .join(' ');
  }

  const quantity =
    Number(config?.quantity) > 0 ? Math.floor(Number(config.quantity)) : 1;

  return {
    key,
    label,
    quantity,
    anyItem,
    maxItemPrice,
    categories,
    menuItems: allowedItems.map((item) => ({
      _id: item._id,
      name: item.name,
      isActive: true,
    })),
    menuSizes,
    subModifiers,
    description,
  };
};

// Returns [buySection, getSection] for the picker. Slot indexes are global:
// buy slots occupy 0..buyQty-1, get slots buyQty..buyQty+getQty-1.
export const buildBogoVoucherSections = (voucher, allMenuItems) => [
  buildSection(voucher?.buyConfig, false, allMenuItems, 'Buy', 'buy'),
  buildSection(voucher?.getConfig, true, allMenuItems, 'Get', 'get'),
];

// Estimate rows for the picker's live discount preview: one row per filled
// slot, in the same shape the BOGO discount engine consumes.
export const buildBogoEstimateRows = (selectedItems) =>
  (selectedItems || []).map((item, index) => {
    let sizeKey = '';
    let price = 0;
    const chosenSize =
      (item.selectedSizes || []).find((size) => Number(size.quantity) > 0) ||
      (item.selectedSizes || [])[0];
    if (chosenSize) {
      sizeKey = chosenSize._id;
      price = Number(chosenSize.price) || 0;
    } else {
      // complex item — chosen size lives in the base modifier's submodifiers
      for (const modifier of item.selectedModifiers || []) {
        if (modifier._id === item.baseModifierId) {
          const chosenSubModifier = (modifier.subModifiers || []).find(
            (subModifier) => Number(subModifier.quantity) > 0
          );
          if (chosenSubModifier) {
            sizeKey = chosenSubModifier._id;
            price = Number(chosenSubModifier.price) || 0;
          }
        }
      }
    }
    return {
      primaryKey: index + 1,
      _id: item._id,
      categoryId: item.categoryId,
      sizeKey,
      price,
      isHalf: false,
      isSecondHalf: false,
    };
  });
